import type { AutoFormInputComponentProps, FormatConfig } from "../types"
import { useTranslations } from "next-intl"
import { useLayoutEffect, useRef, useState } from "react"
import { FormControl, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import AutoFormLabel from "../common/label"
import AutoFormTooltip from "../common/tooltip"

/**
 * Configuración por defecto para códigos de eventos deportivos.
 * Transforma texto a mayúsculas, permite solo alfanuméricos, y valida longitud mínima de 3 caracteres.
 */
const _DEFAULT_CONFIG: FormatConfig = { // El default es para el codigo de un evento deportivo
  transform: (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
  validate: (value: string) => value.length >= 3,
  format: (value: string) => value,
  maxLength: 18,
  minLength: 3,
  placeholder: "Ingrese el texto",
  errorMessage: "El valor debe tener al menos 3 caracteres",
  inputClassName: "",
}

/**
 * FormattedInput - Componente de Input Genérico con Formato Personalizado
 *
 * Un componente reutilizable que permite crear campos de entrada con formato personalizado
 * a través de configuración. Ideal para códigos, números de teléfono, placas, etc.
 *
 * @component
 *
 * @example
 * ```tsx
 * // Ejemplo para número de teléfono
 * formatConfig: {
 *   transform: (value) => value.replace(/\D/g, ""),
 *   format: (value) => {
 *     if (value.length <= 3) return value;
 *     if (value.length <= 6) return `${value.slice(0, 3)}-${value.slice(3)}`;
 *     return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
 *   },
 *   validate: (value) => value.replace(/\D/g, "").length === 10,
 *   maxLength: 10,
 *   placeholder: "123-456-7890"
 * }
 * ```
 *
 * @param {AutoFormInputComponentProps} props - Props del componente
 * @param {string} props.label - Etiqueta del campo
 * @param {boolean} props.isRequired - Si el campo es requerido
 * @param {object} props.fieldConfigItem - Configuración del campo que incluye formatConfig
 * @param {object} props.fieldProps - Props adicionales del campo
 * @param {object} props.field - Objeto del campo de react-hook-form
 *
 * @returns {JSX.Element} Componente de input formateado
 */
export default function FormattedInput({
  label,
  isRequired,
  fieldConfigItem,
  fieldProps,
  field,
}: AutoFormInputComponentProps) {
  const t = useTranslations("formatted_input")

  const DEFAULT_CONFIG: FormatConfig = {
    transform: (value: string) => value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
    validate: (value: string) => value.length >= 3,
    format: (value: string) => value,
    maxLength: 18,
    minLength: 3,
    placeholder: t("placeholder"),
    errorMessage: t("min_length_error", { min: 3 }),
    inputClassName: "",
  }

  const { showLabel: _showLabel, ...fieldPropsWithoutShowLabel } = fieldProps
  const showLabel = _showLabel === undefined ? true : _showLabel
  const inputRef = useRef<HTMLInputElement>(null)
  const [cursor, setCursor] = useState<number | null>(null)

  /** Configuración combinada del campo, mezclando defaults con configuración personalizada */
  const config: FormatConfig = {
    ...DEFAULT_CONFIG,
    ...fieldConfigItem?.inputProps?.formatconfig,
  }

  /**
   * Maneja los cambios en el input aplicando transformaciones, formato y validaciones.
   *
   * @param {string} rawValue - El valor sin procesar del input
   */
  const handleChange = (rawValue: string) => {
    // Aplicar transformación
    const rawCleanValue = config.transform ? config.transform(rawValue) : rawValue

    // Aplicar longitud máxima
    const transformedValue = config.maxLength
      ? rawCleanValue.slice(0, config.maxLength)
      : rawCleanValue

    // Aplicar formato si existe
    let formattedValue = config.format ? config.format(transformedValue) : transformedValue

    // Aplicar relleno si está configurado
    if (config.fillCharacter && config.targetLength) {
      formattedValue = transformedValue + config.fillCharacter.repeat(config.targetLength - transformedValue.length)
    }

    // Actualizar el valor
    field.onChange(formattedValue)

    // Para formatos especiales como duración, no intentar manejar cursor automáticamente
    // El navegador maneja mejor la posición del cursor de forma nativa
    setCursor(null)
  }

  /** Efecto para sincronizar la posición del cursor después de actualizar el valor */
  useLayoutEffect(() => {
    if (inputRef.current && cursor !== null) {
      inputRef.current.setSelectionRange(cursor, cursor)
    }
  }, [field.value, cursor])

  /**
   * Valida el valor actual del campo usando la función de validación configurada.
   * Remueve caracteres de relleno antes de validar para obtener el valor limpio.
   *
   * @returns {boolean} true si el valor es válido, false en caso contrario
   */
  const isValid = () => {
    if (!config.validate)
      return true

    let cleanValue = field.value as string

    // Remover caracteres de relleno para validación
    if (config.fillCharacter) {
      cleanValue = String(cleanValue).replace(new RegExp(config.fillCharacter, "g"), "")
    }

    return config.validate(cleanValue)
  }

  /**
   * Genera el mensaje de error apropiado basado en la configuración.
   * Prioriza el mensaje personalizado, luego genera uno automático basado en minLength/maxLength.
   *
   * @returns {string} El mensaje de error a mostrar
   */
  const getErrorMessage = () => {
    if (config.minLength && config.maxLength) {
      return config.errorMessage || t("range_error", { min: config.minLength, max: config.maxLength })
    }
    if (config.minLength) {
      return config.errorMessage || t("min_length_error", { min: config.minLength })
    }
    return config.errorMessage || t("invalid_value")
  }

  return (
    <div className={cn("flex flex-row items-center space-x-2", fieldConfigItem?.containerClassName)}>
      <FormItem className="flex w-full flex-col justify-start">
        {showLabel && (
          <AutoFormLabel
            isRequired={isRequired}
            label={fieldConfigItem?.label || label}
          />
        )}
        <FormControl>
          <Input
            {...fieldPropsWithoutShowLabel}
            ref={inputRef}
            className={`${config.inputClassName} ${!isValid() && field.value ? "border-red-500" : ""}`}
            onChange={e => handleChange(e.target.value)}
            placeholder={config.placeholder}
            value={field.value || ""}
          />
        </FormControl>
        {!isValid() && field.value && (
          <p className="text-sm text-red-500">
            {getErrorMessage()}
          </p>
        )}
        <AutoFormTooltip fieldConfigItem={fieldConfigItem} />
        <FormMessage />
      </FormItem>
    </div>
  )
}
